import { useRouter } from '@abyss/web/hooks/useRouter';
import { storage } from '@abyss/web/tools/storage';
import { V2Alert } from '@abyss/web/ui/Alert';
import { Box } from '@abyss/web/ui/Box';
import { Button } from '@abyss/web/ui/Button';
import { DropdownMenu } from '@abyss/web/ui/DropdownMenu';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import { find } from 'lodash';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import {
  buildGuestSignInUrl,
  createPostProviderPayload,
  getChipValue,
  getGuestPlanSelectionData,
  getLobFromPortalLOB,
  getProviderCount,
} from '../../frontends/GuestExperience/Guest.utils';
import { findAcquisitionByClientCode } from '../../frontends/GuestExperience/GuestAcquisition.utils';
import { GuestPlanRequiredModal } from '../../frontends/GuestExperience/modals/GuestPlanRequiredModal';
import {
  getClientPortalConfig,
  getGuestThemeColors,
} from '../../frontends/GuestExperience/utils';
import { getActualLanguageCode } from '../../frontends/ProviderSearch/context/Internationalization/helpers';
import {
  LANGUAGES,
  LanguageObject,
  Locale,
} from '../../frontends/ProviderSearch/context/Internationalization/types';
import { useCoverageType } from '../../hooks/useCoverageType';
import { useDeleteGuestAcquisition } from '../../hooks/useDeleteGuestAcquisition';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { usePostProviderInfo } from '../../hooks/usePostProviderInfo';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useGuestAcquisitionStore } from '../../store/useGuestAcquisitionStore';
import { GuestAcquisitionState } from '../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import { useGuestStore } from '../../store/useGuestStore';
import { GuestState } from '../../store/useGuestStore/guestStore';
import { getCurrentMember, getNetworkIdsForPES } from '../../utils/user.utils';
import { ConditionalRender } from '../ConditionalRender/ConditionalRender';
import { ClientNames, Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { UHCSignInBanner } from '../UHCSignInBanner/UHCSignInBanner';
import { FontSizeModal } from './FontSizeModal';
import { GuestLogo } from './GuestLogo';
import { SignIn } from './SignIn';
import { getHeaderContent, usePortalSignInData } from './utils';

interface MenuItem {
  label: string;
  value: Locale;
  checked: boolean;
  onChange: () => void;
}

export const GuestHeader = () => {
  const { t, i18n } = useTranslation();
  const { navigate, getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const lagoonUIMessage = useLagoon('ui-messaging')();
  const configuration = useLagoon('header-configuration')();
  const coverageTypeFromStorage = useCoverageType();
  const [enableGuestHeader, enableUpdatedPSXBannerToSignIn] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_GUEST_HEADER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_UPDATED_PSX_BANNER_TO_SIGN_IN,
  ]);

  // Detect GEHA client using centralized config
  const clientName = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.CLIENT_NAME
  );
  const clientPortalConfig = getClientPortalConfig(clientName);
  const theme = getGuestThemeColors(clientPortalConfig.themeOverride);

  const latestFocusedRef = useRef<Element | null>(null);
  const portalSignInData = usePortalSignInData();

  const {
    selectedPlan,
    lineOfBusiness,
    planYear,
    isDual = false,
  } = getGuestPlanSelectionData();

  const { guestPlanSelectorData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestPlanSelectorData: state.guestPlanSelectorData,
    }))
  );
  const { planName, isGuestDeeplinkFlow, delsysCode } =
    guestPlanSelectorData || {};
  const selectedPlanName = planName || selectedPlan?.planName;

  const lob = getLobFromPortalLOB(lineOfBusiness);

  const [hasUserClosedSignInBanner, setHasUserClosedSignInBanner] = useState(
    sessionStorage.getItem(
      Constants.STORAGE_KEYS.SESSION.IS_GUEST_SIGN_IN_BANNER_DISMISSED
    ) === 'true'
  );

  const coverageTypeFromParams = searchParams?.get('coverageType');
  const chipValueFromParams = searchParams?.get('chipValue');
  const url = window.location.href;
  const isFindcareLoaded = url.includes('/browse');

  const coverage =
    coverageTypeFromParams ||
    (isFindcareLoaded ? coverageTypeFromStorage : 'ALL') ||
    'ALL';
  const lobFromParams = searchParams?.get('planSelectionLob') || '';

  const headerData = useMemo(
    () =>
      getHeaderContent(
        configuration,
        isFindcareLoaded ? lob : lobFromParams,
        coverage
      ),
    [configuration, isFindcareLoaded, lob, lobFromParams, coverage]
  );
  const chipValue = getChipValue(coverage);
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { locale, setLocale } = chipStore;
  const availablePlans: any[] = JSON.parse(
    sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS) ||
      '[]'
  );
  const shouldDisablePlanNavigation =
    planName ||
    (isGuestDeeplinkFlow && availablePlans?.length === 1 && !!delsysCode);

  const { requestData, responseData, isNavigatingMaestro, setResponseData } =
    useGuestAcquisitionStore(
      useShallow((state: GuestAcquisitionState) => ({
        requestData: state.requestData,
        responseData: state.responseData,
        setResponseData: state.setResponseData,
        isNavigatingMaestro: state.isNavigatingMaestro,
      }))
    );
  const acquisitionData = useLagoon('acquisition')?.();
  const matchedAcquisition = findAcquisitionByClientCode(
    acquisitionData,
    requestData?.client || ''
  );
  const providerCount = getProviderCount(responseData);

  const [, deleteGuestAcquisition] = useDeleteGuestAcquisition({});
  const [, postProviderInfo] = usePostProviderInfo({});
  const featureFlags = useLagoon('feature-flags')();
  const currentMember = getCurrentMember();

  const handleFinishAndReturn = async () => {
    const reciprocityId = getNetworkIdsForPES(
      currentMember,
      coverage,
      featureFlags
    );

    const payload = createPostProviderPayload(
      requestData,
      responseData,
      selectedPlan,
      coverage,
      reciprocityId
    );

    const result = await postProviderInfo({
      variables: {
        ...payload,
        upstreamEnv: requestData?.upstreamEnv,
        targetUrl: requestData?.targetUrl,
      },
    });

    const html = result?.data?.postProviderInfo;
    if (html) {
      setResponseData([]);
      const transactionId = requestData?.transactionId;
      if (transactionId) {
        await deleteGuestAcquisition({
          variables: { transactionId },
        });
      }
      document.open();
      document.write(html);
      document.close();
    }
  };

  const signInConfig = useMemo(() => {
    const config = find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION.GUEST_HEADER_SIGN_IN,
    });

    let signInUrl = headerData?.signinUrl || config?.url;

    if (portalSignInData.shouldUsePortalSignInRoute) {
      signInUrl = portalSignInData.portalSignInRoute;
    }

    const finalSignInUrl = buildGuestSignInUrl(
      signInUrl,
      locale,
      coverageTypeFromParams || '',
      chipValueFromParams || ''
    );

    // Save the computed sign-in URL to sessionStorage for use in other components
    sessionStorage.setItem(
      Constants.STORAGE_KEYS.SESSION.GUEST_SIGN_IN_URL,
      finalSignInUrl
    );

    return {
      shouldShowSignIn: portalSignInData.shouldShowSignIn,
      text: config?.message,
      url: finalSignInUrl,
    };
  }, [
    lagoonUIMessage,
    headerData?.signinUrl,
    i18n.language,
    portalSignInData,
    locale,
  ]);

  const uhcContinueConfig = useMemo(() => {
    const config = find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION
        .GUEST_HEADER_PREPLAN_UHC_CONTINUE,
    });
    return {
      text: config?.message,
      url: config?.url || 'https://www.uhc.com/find-a-doctor',
    };
  }, [lagoonUIMessage]);

  const signInBannerConfig = useMemo(() => {
    const bannerText = find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION.GUEST_SIGN_IN_BANNER,
    })?.message;

    const bannerButtonData = find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION.GUEST_SIGN_IN_BANNER_BUTTON,
    });

    let url = headerData?.signinUrl || bannerButtonData?.url;

    if (portalSignInData.shouldUsePortalSignInRoute) {
      url = portalSignInData.portalSignInRoute;
    }

    const signInUrl: string | null = buildGuestSignInUrl(
      url,
      locale,
      coverageTypeFromParams || '',
      chipValueFromParams || ''
    );

    return {
      text: bannerText,
      buttonText: bannerButtonData?.message,
      url: signInUrl,
    };
  }, [
    lagoonUIMessage,
    headerData?.signinUrl,
    i18n.language,
    locale,
    portalSignInData,
  ]);

  const supportedLanguages = headerData?.languages
    .split(',')
    .map((lang) => lang.trim()) || [Locale.enUS, Locale.esUS];

  const updateLanguage = useCallback(
    (chosenLanguage: LanguageObject) => {
      storage.cookie.set(Constants.PSLOCALE, chosenLanguage.locale);
      setLocale(chosenLanguage.locale);
      i18n.changeLanguage(chosenLanguage.locale);
    },
    [setLocale]
  );

  // Initialize language once on mount from persisted cookie value
  // All subsequent changes are handled by handleOnLanguageChange from the dropdown
  useLayoutEffect(() => {
    const cookieLocale = storage.cookie.get(Constants.PSLOCALE);
    const initialLocale = locale || cookieLocale || Locale.enUS;

    // Initialize with persisted language
    const chosenLanguage =
      LANGUAGES[
        getActualLanguageCode(initialLocale as Locale, supportedLanguages)
      ];

    // Only update if different to avoid unnecessary re-initialization
    if (chosenLanguage.locale !== i18n.language) {
      updateLanguage(chosenLanguage);
    }
  }, []); // Run only once on mount - dropdown changes handled by handleOnLanguageChange

  const [isPlanRequiredModalOpen, setIsPlanRequiredModalOpen] = useState(false);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  const handleOnLanguageChange = async (locale: Locale) => {
    // Prevent multiple rapid language changes
    if (isChangingLanguage) return;

    setIsChangingLanguage(true);

    const chosenLanguage =
      LANGUAGES[getActualLanguageCode(locale, supportedLanguages)];
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SUPPORTED_LANGUAGES,
      JSON.stringify(supportedLanguages)
    );
    updateLanguage(chosenLanguage);

    // Update URL parameter
    const url = new URL(window.location.href);
    url.searchParams.set('locale', chosenLanguage.locale);
    // Remove legacy 'lang' param if present to avoid confusion
    url.searchParams.delete('lang');
    window.history.pushState({}, '', url.toString());

    // Only reload on /browse page where provider search results are already loaded
    // Early pages (select-coverage-type, etc.) use Lagoon which auto-refetches via LagoonProvider
    if (isFindcareLoaded) {
      window.location.reload();
    } else {
      setIsChangingLanguage(false);
    }
  };

  const logoClick = () => {
    // If on FindCare browse, keep existing redirect logic

    latestFocusedRef.current = document.activeElement;
    if (isFindcareLoaded) {
      window.location.assign(
        `/guest-plan-selection/browse?chipValue=${chipValue}&coverageType=${coverage}`
      );
      return;
    }
    // Otherwise, if sign-in allowed, open the modal
    if (signInConfig.shouldShowSignIn) {
      setIsPlanRequiredModalOpen(true);
    }
  };

  const handlePlanRequiredClose = () => {
    setIsPlanRequiredModalOpen(false);

    setTimeout(() => {
      (latestFocusedRef.current as HTMLElement)?.focus();
    }, 500);
  };

  const languageMenuItems = [
    {
      checkboxes: Object?.values(LANGUAGES)?.reduce<MenuItem[]>((acc, item) => {
        if (supportedLanguages?.includes(item?.locale)) {
          acc.push({
            label: item.title,
            value: item.locale,
            checked: locale === item.locale,
            onChange: () => {
              handleOnLanguageChange(item?.locale);
            },
          });
        }
        return acc;
      }, []),
    },
  ];

  if (!enableGuestHeader) {
    return null;
  }

  const getHyperLink = (
    e: ReactMouseEvent<HTMLAnchorElement>,
    locale: string
  ) => {
    e.preventDefault();
    switch (locale) {
      case Locale.enUS:
        return window.open(
          'https://www.uhc.com/contact-us?locale=en-US',
          '_blank'
        );
      case Locale.esUS:
        return window.open('https://es.uhc.com/contact-us', '_blank');
      default:
        return window.open(
          'https://www.uhc.com/contact-us?locale=en-US',
          '_blank'
        );
    }
  };

  return (
    <Box
      css={{
        'abyss-box-root': {
          position: 'sticky',
          top: 0,
          zIndex: 11,
          backgroundColor: '$white',
          width: '100%',
          height: 'auto',
          opacity: 1,
          fontFamily: '$primary',
          marginTop: '0px',
          padding: '0px',
        },
      }}
    >
      <Flex
        css={{
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <Flex
          css={{
            justifyContent: 'center',
            backgroundColor: theme.utilityBarBg,
            width: '100%',
          }}
        >
          <Flex
            css={{
              gap: '16px',
              width: '100%',
              maxWidth: '1244px',
              justifyContent: 'flex-end',
              padding: '0 24px 0 0',
            }}
          >
            <DropdownMenu
              after={
                <IconSymbol
                  color={theme.textColor}
                  icon="keyboard_arrow_down"
                />
              }
              css={{
                fontSize: '$sm',
                color: `${theme.textColor} !important`,
                'abyss-dropdown-menu-trigger': {
                  backgroundColor: 'transparent',
                  color: `${theme.textColor} !important`,
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                  '&[data-state="open"]': {
                    backgroundColor: 'white',
                  },
                },
                '.abyss-dropdown-menu-trigger': {
                  color: `${theme.textColor} !important`,
                },
                '& button': {
                  color: `${theme.textColor} !important`,
                },
                '& span': {
                  color: `${theme.textColor} !important`,
                },
              }}
              data-testid="language-dropdown"
              label={t('Language')}
              menuItems={languageMenuItems}
              modal={false}
              outline={false}
              width={110}
            />
            <FontSizeModal
              buttonBgColor={theme.primaryColor}
              linkColor={theme.linkColor}
              textColor={theme.textColor}
            />
            <ConditionalRender
              client={{ hideFor: [ClientNames.GEHA, ClientNames.UMR] }}
            >
              <Link
                css={{
                  fontSize: '$sm',
                  textDecoration: 'none',
                  color: `${theme.linkColor} !important`,
                  '& .abyss-link-icon': { display: 'none' },
                }}
                data-testid="help-link"
                href={'#'}
                onClick={(e) => getHyperLink(e, locale)}
                openNewWindow={true}
              >
                {t('HELP')}
              </Link>
            </ConditionalRender>
          </Flex>
        </Flex>
        <Flex
          css={{
            justifyContent: 'center',
            alignItems: 'center',
            height: '64px',
            padding: '8px 16px',
            width: '100%',
          }}
        >
          <Flex
            css={{
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              maxWidth: '1272px',
            }}
          >
            <GuestLogo headerData={headerData} logoClick={logoClick} />
            {signInConfig.shouldShowSignIn &&
              !matchedAcquisition?.hideSignInHeader && (
                <SignIn text={signInConfig.text} url={signInConfig.url} />
              )}
          </Flex>
        </Flex>
        <Flex
          css={{
            justifyContent: 'center',
            height: 'var(--abyss-psx-parcel-space-xl)',
            backgroundColor: theme.primaryColor,
            color: '#ffffff',
            padding: '8px 16px',
            width: '100%',
          }}
        >
          {((isFindcareLoaded && Boolean(selectedPlanName || planYear)) ||
            (isGuestDeeplinkFlow && Boolean(planName))) && (
            <Box
              css={{
                alignItems: 'center',
                flexDirection: 'row',
                display: 'flex',
                backgroundColor: theme.primaryColor,
                color: '#ffffff',
                gap: '8px',
                maxWidth: '1272px',
                padding: 0,
              }}
            >
              <Flex
                css={{
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '$sm',
                  fontWeight: 500,
                }}
              >
                <span style={{ fontWeight: 700 }}>{t('PLAN')}</span>
                <span
                  data-testid="guest-plan-name-link"
                  onClick={() => {
                    if (shouldDisablePlanNavigation) {
                      return;
                    }
                    navigate(
                      `/plan-selection?planSelectionLob=${lob}&coverageType=${
                        coverage || 'M'
                      }&chipValue=${chipValue}`
                    );
                  }}
                  onKeyDown={(e) => {
                    if (shouldDisablePlanNavigation) {
                      return;
                    }
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(
                        `/plan-selection?planSelectionLob=${lob}&coverageType=${
                          coverage || 'M'
                        }&chipValue=${chipValue}`
                      );
                    }
                  }}
                  role="link"
                  style={{
                    cursor: 'pointer',
                    color: '#ffffff',
                  }}
                  tabIndex={0}
                >
                  {selectedPlanName}
                </span>
              </Flex>
              {isNavigatingMaestro && (
                <Flex
                  css={{
                    marginLeft: 'auto',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <Text css={{ color: '#ffffff' }}>
                    {`${providerCount} ${
                      providerCount > 1 ? t('Providers') : t('Provider')
                    } ${t('Selected')}`}
                  </Text>
                  <Button
                    after={<IconSymbol icon="open_in_new" size="20" />}
                    disabled={providerCount <= 0}
                    onClick={handleFinishAndReturn}
                    size="sm"
                    variant="outline"
                  >
                    {t('Finish_and_return')}
                  </Button>
                </Flex>
              )}
              {isDual && (
                <>
                  <IconSymbol
                    color="#ffffff"
                    fill="#ffffff"
                    icon="arrow_forward_ios"
                    size="16px"
                  />
                  <Flex
                    css={{
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '$sm',
                      fontWeight: 500,
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{t('YEAR')}</span>
                    <span
                      data-testid="guest-plan-year-link"
                      onClick={() => {
                        navigate(
                          `/plan-year-selection?planSelectionLob=${lob}&coverageType=${
                            coverage || 'M'
                          }&chipValue=${chipValue}`
                        );
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(
                            `/plan-year-selection?planSelectionLob=${lob}&coverageType=${
                              coverage || 'M'
                            }&chipValue=${chipValue}`
                          );
                        }
                      }}
                      role="link"
                      style={{
                        cursor: 'pointer',
                        color: '#ffffff',
                      }}
                      tabIndex={0}
                    >
                      {planYear}
                    </span>
                  </Flex>
                </>
              )}
            </Box>
          )}
        </Flex>
      </Flex>

      {!isGuestDeeplinkFlow &&
        !matchedAcquisition?.hideSignInHeader &&
        enableUpdatedPSXBannerToSignIn && (
          <UHCSignInBanner signInConfig={signInConfig} />
        )}

      {isFindcareLoaded &&
        !hasUserClosedSignInBanner &&
        !matchedAcquisition?.hideSignInHeader &&
        portalSignInData.shouldShowSignIn && (
          <V2Alert
            cta={{
              type: 'button',
              props: {
                children: signInBannerConfig.buttonText,
                onClick: () => {
                  if (!signInBannerConfig.url) {
                    // eslint-disable-next-line no-console
                    console.error('No sign in url provided');
                  } else {
                    window.location.href = signInBannerConfig.url;
                  }
                },
              },
            }}
            data-testid="guest-sign-in-banner"
            onClose={() => {
              setHasUserClosedSignInBanner(true);
              sessionStorage.setItem(
                Constants.STORAGE_KEYS.SESSION
                  .IS_GUEST_SIGN_IN_BANNER_DISMISSED,
                'true'
              );
            }}
            status="info"
          >
            {signInBannerConfig.text}
          </V2Alert>
        )}
      {isPlanRequiredModalOpen && (
        <GuestPlanRequiredModal
          isOpen={isPlanRequiredModalOpen}
          onClose={handlePlanRequiredClose}
          signInUrl={signInConfig.url || '#'}
          uhcContinueUrl={uhcContinueConfig.url}
        />
      )}
    </Box>
  );
};

export default GuestHeader;
